import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { registerServiceWorker } from './sw/register';
import { captureInstallPrompt } from './install-prompt';
import { initPostHog } from './observability/posthog';

// P20 — captura beforeinstallprompt ANTES de React. Ver G61: evento dispara 1×
// rápido; se chegar depois de React montar + bundle carregar, perdemos.
// Primeira coisa no entry, inclusive antes de Sentry (custo desprezível).
captureInstallPrompt();

// Sentry init — só liga em build prod com DSN setado. Em dev fica quieto
// pra não poluir o dashboard com erros HMR/dev-only. CSP já libera
// connect-src https://*.sentry.io (ver public/_headers).
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (import.meta.env.PROD && SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'staging',
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay conservador no launch: 10% das sessões, 100% em sessões
      // com erro. Captura contexto visual útil sem explodir o quota.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1',
    ),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Shell does NOT mount a top-level <BrowserRouter>. Two reasons:
// 1. Bundles loaded under /app/:slug/* mount their OWN <BrowserRouter
//    basename={`/app/${slug}`}> via @hook-sdk/template's <AppRoot>.
//    Nested Routers throw in react-router v6 ("You cannot render a
//    <Router> inside another <Router>"), and the inner one is the
//    contract apps build against (basename + auth gate + deep links).
// 2. Shell's own routes (Home, Producer, Admin, Legal) live in App.tsx,
//    which mounts BrowserRouter only for non-/app paths — see the
//    conditional there. End-users never SPA-navigate between an app
//    bundle and a shell route, so the full-page transition is fine.

// PostHog init — lazy via requestIdleCallback (não bloqueia paint). No-op se
// VITE_POSTHOG_KEY ausente ou !PROD. Boot order: captureInstallPrompt FIRST
// (ADR-017 — pre-React event), Sentry, PostHog, ReactDOM.render. Ver ADR-019
// e docs/superpowers/specs/2026-04-25-posthog-multi-app-design.md.
initPostHog();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// P15: registra SW só em build prod (Vite PROD=true). Dev: testar via preview.
if (import.meta.env.PROD) {
  registerServiceWorker();
}
